import { apiFetch } from './client.js';

/**
 * A customer's sign-in details, changed on their request.
 *
 * Both calls end every session the customer has, and both are recorded on the
 * customer's case file with the reason given here — write the reason as the
 * compliance reviewer will read it.
 */

export interface EmailChangeResult {
  /** True when the customer was emailed a security notice (DEV-134). */
  customerNotified?: boolean;
  ok: boolean;
  email: string;
  sessionsRevoked: boolean;
}

export interface PasswordResetResult {
  /** True when the customer was emailed a security notice (DEV-134). */
  customerNotified?: boolean;
  ok: boolean;
  /** Shown once. It is not stored anywhere in a form anybody can read back. */
  temporaryPassword: string;
  mustChangeOnNextSignIn: boolean;
  sessionsRevoked: boolean;
}

export interface AuthenticatorState {
  enrolled: boolean;
  enabledAt: string | null;
}

export interface AuthenticatorResetResult {
  /** True when the customer was emailed a security notice (DEV-134). */
  customerNotified?: boolean;
  ok: boolean;
  /** False when none was active (pending setups and lockouts were still cleared). */
  removed: boolean;
  sessionsRevoked: boolean;
}

export const customerCredentialsApi = {
  /** Whether the customer has Google Authenticator set up, and since when (DEV-134). */
  authenticator: (customerId: string): Promise<AuthenticatorState> =>
    apiFetch<AuthenticatorState>(`/admin/customers/${customerId}/credentials/authenticator`),

  /** Remove the customer's Google Authenticator on request (DEV-134). Ends every session. */
  resetAuthenticator: (customerId: string, reason: string): Promise<AuthenticatorResetResult> =>
    apiFetch<AuthenticatorResetResult>(`/admin/customers/${customerId}/credentials/authenticator/reset`, {
      method: 'POST',
      body: { reason },
    }),

  changeEmail: (customerId: string, email: string, reason: string): Promise<EmailChangeResult> =>
    apiFetch<EmailChangeResult>(`/admin/customers/${customerId}/credentials/email`, {
      method: 'POST',
      body: { email, reason },
    }),

  resetPassword: (customerId: string, reason: string): Promise<PasswordResetResult> =>
    apiFetch<PasswordResetResult>(`/admin/customers/${customerId}/credentials/password`, {
      method: 'POST',
      body: { reason },
    }),
};
